import { useEffect, useRef, useCallback } from "react";
import messageTone from "../assets/notificacion/message-tone-2870.wav";
import acceptSound from "../assets/notificacion/aceptacion.wav";
import timeoutSound from "../assets/notificacion/expiracion.wav";
import finishSound from "../assets/notificacion/finalizacion.mp3";
import rejectSound from "../assets/notificacion/rechazo.wav";

/**
 * Hook personalizado para reproducir sonidos de notificación
 * @returns {Object} - { playSound, stopSound }
 * @example
 * const { playSound, stopSound } = useNotificationSound();
 * playSound({ count: 2, interval: 1500 }); // 2 repeticiones cada 1.5s
 * playSound(); // 1 repetición con intervalo por defecto
 */
const sounds = {
    message: messageTone,
    accept: acceptSound,
    reject: rejectSound,
    timeout: timeoutSound,
    finish: finishSound,
  };

export const useNotificationSound = () => {
  const audioRef = useRef(null);
  const loopTimeoutRef = useRef(null);
  const isPlayingRef = useRef(false);

  useEffect(() => {
    const audio = new Audio(messageTone);
    audio.loop = false;
    audioRef.current = audio;

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      if (loopTimeoutRef.current) {
        clearTimeout(loopTimeoutRef.current);
        loopTimeoutRef.current = null;
      }
    };
  }, []);

  /**
   * Reproduce el sonido de notificación
   * @param {Object} config - Configuración de reproducción
   * @param {number} config.count - Número de repeticiones (1-10, default: 1)
   * @param {number} config.interval - Intervalo entre repeticiones en ms (min: 500ms, default: 2000ms)
   */
  const playSound = useCallback(
    ({
      sound = "message",
      count = 1,
      interval = 2000 } = {}) => {
      // Validar y sanitizar parámetros
      const validCount = Math.max(1, Math.floor(count) || 1);
      const validInterval = Math.max(500, Math.floor(interval) || 2000);

      if (!audioRef.current) {
        console.warn("⚠️ useNotificationSound: Audio no inicializado");
        return;
      }

      // Cancelar reproducción anterior si está en curso
      if (loopTimeoutRef.current) {
        clearTimeout(loopTimeoutRef.current);
        loopTimeoutRef.current = null;
      }

      isPlayingRef.current = true;
      let playCount = 0;

      const playRepeat = () => {
        if (playCount < validCount && isPlayingRef.current) {
          audioRef.current.src = sounds[sound] || messageTone;
          audioRef.current.load();
          audioRef.current.currentTime = 0;

          audioRef.current
            .play()
            .then(() => {
              playCount++;

              if (playCount < validCount && isPlayingRef.current) {
                loopTimeoutRef.current = setTimeout(playRepeat, validInterval);
              } else {
                isPlayingRef.current = false;
              }
            })
            .catch((error) => {
              console.error(
                "❌ Error reproduciendo sonido:",
                error.message || error,
              );
              isPlayingRef.current = false;
            });
        } else {
          isPlayingRef.current = false;
        }
      };

      playRepeat();
    },
    [],
  );

  const stopSound = useCallback(() => {
    isPlayingRef.current = false;

    if (loopTimeoutRef.current) {
      clearTimeout(loopTimeoutRef.current);
      loopTimeoutRef.current = null;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, []);

  return {
    playSound,
    stopSound,
  };
};